import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../api/client.ts';

/**
 * Load something, and keep what it said.
 *
 * The keeping is the point. When the connection drops, screen 18 says to show
 * what was true a moment ago and turn off the buttons that start work — so a
 * failed reload must not blank the screen. `data` stays, `offlineSince` is set,
 * and the screen draws the banner over the data it already had.
 */

export type Loaded<T> =
  | { status: 'loading'; data: T | null }
  | { status: 'ready'; data: T }
  | { status: 'offline'; data: T | null; since: Date }
  | { status: 'failed'; data: T | null; error: unknown };

export interface LoadResult<T> {
  state: Loaded<T>;
  reload: () => void;
  /** Replace what is held, for when a write already told us the new answer. */
  set: (data: T) => void;
}

export function useLoad<T>(
  load: () => Promise<T>,
  deps: readonly unknown[]
): LoadResult<T> {
  const [state, setState] = useState<Loaded<T>>({ status: 'loading', data: null });
  const held = useRef<T | null>(null);
  // The load function changes identity on every render; the deps are what
  // actually decide when to load again.
  const latest = useRef(load);
  latest.current = load;

  const run = useCallback(async () => {
    setState({ status: 'loading', data: held.current });
    try {
      const data = await latest.current();
      held.current = data;
      setState({ status: 'ready', data });
    } catch (err) {
      if (err instanceof ApiError && err.offline) {
        setState({ status: 'offline', data: held.current, since: new Date() });
      } else {
        setState({ status: 'failed', data: held.current, error: err });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const set = useCallback((data: T) => {
    held.current = data;
    setState({ status: 'ready', data });
  }, []);

  return { state, reload: run, set };
}
